import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBookmark,
  faBookOpen,
  faCheck,
  faTimes,
  faUsers,
  faGlobe,
  faTags,
  faNoteSticky,
  faList,
  faStar,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import { useT } from "../../i18n/I18nContext";
import { useAuth } from "../../auth/AuthContext";
import { addToShelf } from "../../util/readingsApi";
import { apiClient } from "../../util/apiClient";
import { logger } from "../../util/logger";
import BookCover from "../Shared/BookCover";

// Project preview modal mounted by the catalogue / browse pages.
//
// The modal opens on a card click and lazily fetches the *full* project
// payload via ``GET /api/projects/<id>/`` (which sets
// ``include_chapters=True`` server-side) so it can render:
//
//   * Hero block — cover, title, status pill, total parts, description.
//   * Story details — genre, category, target audience, language,
//     copyright, tags, characters. Each is a small icon medallion +
//     label/value row, matching the Settings card visual rhythm.
//   * Story notes — free-form prewriting scratch the writer chose to
//     surface. Plain text, ``whitespace-pre-wrap``.
//   * Table of contents — chapter list with title + word count.
//
// Sections that have no data (no characters, no tags, no notes, no
// chapters) are dropped entirely — the modal stays compact for
// minimal projects without rendering "—" placeholders everywhere.
const Modal = ({
  setOpenModal,
  openModal,
  status,
  img,
  title,
  totalParts,
  description,
  projectId,
}) => {
  const t = useT();
  const navigate = useNavigate();
  const { status: authStatus } = useAuth();
  const isSignedIn = authStatus === "signedIn";
  // ``shelfState`` covers the full per-modal lifecycle:
  //   * idle      — no shelf action attempted
  //   * saving    — POST in flight
  //   * added     — POST returned ``created: true`` (fresh add)
  //   * existing  — POST returned ``created: false`` (already on shelf)
  //   * error     — POST threw
  // Both ``added`` and ``existing`` render the same disabled "in
  // shelf" CTA, but the copy + transient toast differ so the writer
  // sees feedback that matches what actually happened.
  const [shelfState, setShelfState] = useState("idle");
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpenModal(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpenModal]);

  // Lazily fetch the rich project payload when the modal opens. The
  // detail endpoint includes ``main_characters``, ``story_notes``,
  // ``tags``, ``category``, ``target_audience``, ``language``,
  // ``copyright``, ``chapters[]`` — everything the rich preview
  // needs. Without this the modal would only show what the catalogue
  // card already passed in (title / cover / description).
  //
  // ``shelfState`` and ``detail`` both reset here on every
  // ``projectId`` change. Without the reset:
  //   * the previous project's chapter list / notes flashed in the
  //     hero+sections during the new fetch (stale visual state);
  //   * the "in shelf" CTA stayed locked-in if the previous project
  //     had been added — the next project then misreported as
  //     already-on-shelf before the writer touched anything.
  useEffect(() => {
    setDetail(null);
    setShelfState("idle");
    if (!openModal || !projectId) return undefined;
    let cancelled = false;
    setLoadingDetail(true);
    (async () => {
      try {
        const { data } = await apiClient.get(`/api/projects/${projectId}/`);
        if (cancelled) return;
        setDetail(data || null);
        // Backend ships ``is_on_shelf`` in the per-viewer detail
        // payload. Prime the shelf CTA to its "already added" state
        // up front so the button reads correctly the moment the
        // modal lands — without this the writer would see "Add to
        // reading list" on a project they've already shelved and
        // only learn the truth after clicking.
        if (data?.is_on_shelf) setShelfState("existing");
      } catch (err) {
        logger.warn("project_detail_unavailable", err?.message);
        if (!cancelled) setDetail(null);
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openModal, projectId]);

  const handleDiscover = () => {
    if (!projectId) return;
    setOpenModal(false);
    navigate(`/read/${projectId}`);
  };

  const handleAddToShelf = async () => {
    if (
      !projectId ||
      shelfState === "saving" ||
      shelfState === "added" ||
      shelfState === "existing"
    )
      return;
    if (!isSignedIn) {
      navigate(`/login?next=/read/${projectId}`);
      return;
    }
    setShelfState("saving");
    try {
      // Backend now returns ``{created: true|false, ...row}`` so we
      // can show different copy for a fresh add vs an idempotent
      // re-add of an already-on-shelf project. Defaults to "added"
      // for older backends that omit the flag.
      const data = await addToShelf(projectId);
      setShelfState(data?.created === false ? "existing" : "added");
    } catch (err) {
      logger.warn("shelf_add_failed", err?.message);
      setShelfState("error");
    }
  };

  // Prefer the freshly-fetched description, but treat an empty
  // string as "no value" so the prop description (which the
  // catalogue card already rendered) shows through when the project
  // was created without one. Using ``??`` instead of ``||`` would
  // let an empty string from the server override the prop.
  const safeDescription =
    (detail?.description && detail.description.trim()) ||
    description ||
    "";
  // Status: prefer the structured ``detail.archived`` boolean once
  // the detail payload has landed. The ``status`` prop is a
  // pre-translated label string from the catalogue card — fragile
  // across locale switches (a writer flipping FR↔EN with the modal
  // open would briefly see the wrong pill) and inaccurate when the
  // prop is empty. Fall through to the prop label only as a
  // before-detail hint.
  const inProgressLabel = t("home.status.in_progress");
  const isComplete = detail
    ? Boolean(detail.archived)
    : !!status && status !== inProgressLabel;

  // Lock body scroll while open.
  useEffect(() => {
    if (!openModal) return undefined;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [openModal]);

  if (!openModal || typeof document === "undefined") return null;

  // Story-details rows: only the ones that have a value. Each row is
  // a 9px icon medallion + small label + value, the same visual
  // pattern Settings → Member since uses, so the modal sits in the
  // app's design language without inventing a new component.
  //
  // Localize the *value* side too — CreateProject stores enum-style
  // English strings ("Fiction", "French", "Adults") so the raw
  // value rendered "French" for a FR-locale reader instead of
  // "Français". The localizeEnumValue helper maps each stored
  // value back to the i18n key the picker used at write time, then
  // resolves it via t(); unknown values fall through to the raw
  // input so a free-text genre still renders something.
  const detailRows = [];
  if (detail?.genre)
    detailRows.push({
      icon: faBookOpen,
      labelKey: "home.book.modal.detail.genre",
      value: localizeEnumValue(t, "genre", detail.genre),
    });
  if (detail?.category)
    detailRows.push({
      icon: faTags,
      labelKey: "home.book.modal.detail.category",
      value: localizeEnumValue(t, "category", detail.category),
    });
  if (detail?.target_audience)
    detailRows.push({
      icon: faUsers,
      labelKey: "home.book.modal.detail.audience",
      value: localizeEnumValue(t, "audience", detail.target_audience),
    });
  if (detail?.language)
    detailRows.push({
      icon: faGlobe,
      labelKey: "home.book.modal.detail.language",
      value: localizeEnumValue(t, "language", detail.language),
    });

  const tags = Array.isArray(detail?.tags) ? detail.tags.filter(Boolean) : [];
  // The browse modal's "Main characters" section should only list
  // characters the writer marked as central to the story —
  // ``supporting`` and ``minor`` roles are deliberately excluded.
  // Characters without a role default to "main" (the writer hasn't
  // categorised them yet) so we don't blank a project that has
  // characters but no planner curation. The role values come from
  // the planner catalogue: protagonist / antagonist / mentor / ally
  // / foil / supporting / minor / "".
  const NON_MAIN_ROLES = new Set(["supporting", "minor"]);
  const characters = Array.isArray(detail?.main_characters)
    ? detail.main_characters.filter((c) => {
        const role = (c?.role || "").toLowerCase();
        return !NON_MAIN_ROLES.has(role);
      })
    : [];
  const chapters = Array.isArray(detail?.chapters) ? detail.chapters : [];
  const notes = (detail?.story_notes || "").trim();
  const ratingAverage = Number(detail?.rating_average || 0);
  const ratingCount = Number(detail?.rating_count || 0);
  const readerCount = Number(detail?.reader_count || 0);
  // This modal is opened from public surfaces (CatalogueShowcase,
  // recommendations) — show the count readers can actually open.
  // ``published_chapter_count`` is the source of truth; the legacy
  // ``chapter_count`` + chapters.length + totalParts fallbacks
  // cover older API responses without the new field.
  const partsCount =
    detail?.published_chapter_count
    ?? detail?.chapter_count
    ?? chapters.length
    ?? totalParts
    ?? 0;
  const cover = detail?.cover_url || img;

  return createPortal(
    // Backdrop dismiss; Escape handled by parent state — keyboard
    // users keep an exit path via the close button (line 269).
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <div
      className="fixed inset-0 flex justify-center items-center z-[80] bg-black/40 backdrop-blur-sm p-4"
      onClick={() => setOpenModal(false)}
      role="dialog"
      aria-modal="true"
    >
      {/* Inner card stops propagation so internal clicks don't close. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="relative bg-surface border border-border rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto custom-scrollbar"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label={t("home.book.modal.close")}
          onClick={() => setOpenModal(false)}
          className="absolute top-3 right-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full text-secondText bg-surface/80 backdrop-blur-sm hover:bg-surfaceAlt hover:text-text transition"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>

        {/* Hero */}
        <div className="flex flex-col lg:flex-row gap-6 p-6 lg:p-8">
          <BookCover
            src={cover}
            alt=""
            className="w-full lg:w-1/3 aspect-[3/4] object-cover rounded-lg shrink-0"
            iconClassName="text-secondText text-5xl"
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-2xl text-text mb-2 break-words">
              {detail?.title || title}
            </h3>
            <div className="flex flex-wrap gap-2 items-center mb-3">
              <span
                className={`border rounded-full py-1 px-3 text-xs font-semibold ${
                  isComplete
                    ? "border-success text-success"
                    : "border-primary text-primary"
                }`}
              >
                {isComplete
                  ? t("home.book.modal.status.complete")
                  : t("home.book.modal.status.ongoing")}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-secondText">
                <FontAwesomeIcon icon={faList} className="text-[10px]" />
                {t("home.book.modal.total_parts", { count: partsCount })}
              </span>
              {ratingCount > 0 ? (
                <span className="inline-flex items-center gap-1 text-xs text-secondText">
                  <FontAwesomeIcon icon={faStar} className="text-warning text-[10px]" />
                  {ratingAverage.toFixed(1)} · {ratingCount}
                </span>
              ) : null}
              {readerCount > 0 ? (
                <span className="inline-flex items-center gap-1 text-xs text-secondText">
                  <FontAwesomeIcon icon={faEye} className="text-[10px]" />
                  {readerCount}
                </span>
              ) : null}
            </div>
            {safeDescription ? (
              <p className="text-sm text-text leading-relaxed mb-4 whitespace-pre-wrap">
                {safeDescription}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-3 items-center">
              <button
                type="button"
                onClick={handleDiscover}
                className="inline-flex items-center gap-2 bg-primary text-white font-semibold py-2.5 px-5 rounded-full hover:brightness-110 transition"
              >
                <FontAwesomeIcon icon={faBookOpen} />
                {t("home.book.modal.discover")}
              </button>
              <button
                type="button"
                onClick={handleAddToShelf}
                disabled={
                  shelfState === "saving" ||
                  shelfState === "added" ||
                  shelfState === "existing"
                }
                className={`inline-flex items-center gap-2 py-2.5 px-5 rounded-full border font-semibold transition ${
                  shelfState === "added" || shelfState === "existing"
                    ? "border-success text-success bg-success/10 cursor-default"
                    : "border-border text-text hover:bg-surfaceAlt"
                } disabled:opacity-80`}
              >
                <FontAwesomeIcon
                  icon={
                    shelfState === "added" || shelfState === "existing"
                      ? faCheck
                      : faBookmark
                  }
                />
                {shelfState === "added"
                  ? t("home.book.modal.in_shelf")
                  : shelfState === "existing"
                  ? t("home.book.modal.already_in_shelf")
                  : t("home.book.modal.add_to_shelf")}
              </button>
              {shelfState === "error" && (
                <p className="w-full text-sm text-danger" role="alert">
                  {t("home.book.modal.shelf_failed")}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Sections below the hero — story details, characters,
            tags, story notes, table of contents. Each is rendered
            only if it has data, with a separator above. The padding
            mirrors the Settings card subsection rhythm. */}
        <div className="px-6 lg:px-8 pb-8">
          {loadingDetail && !detail ? (
            <div className="border-t border-border pt-6 text-sm text-secondText">
              {t("home.book.modal.loading")}
            </div>
          ) : null}

          {/* Story details */}
          {detailRows.length > 0 ? (
            <section className="border-t border-border pt-6 mt-6">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-secondText mb-3">
                {t("home.book.modal.section.details")}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {detailRows.map((row) => (
                  <div key={row.labelKey} className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primaryMuted text-primary shrink-0">
                      <FontAwesomeIcon icon={row.icon} className="text-sm" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[11px] text-secondText">
                        {t(row.labelKey)}
                      </p>
                      <p className="text-sm text-text font-medium truncate">
                        {row.value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {/* Characters */}
          {characters.length > 0 ? (
            <section className="border-t border-border pt-6 mt-6">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-secondText mb-3">
                {t("home.book.modal.section.characters")}
              </h4>
              <div className="flex flex-wrap gap-2">
                {characters.map((c, i) => (
                  <span
                    key={c.id ?? i}
                    className="inline-flex items-center gap-1.5 rounded-full bg-surfaceAlt text-text border border-border px-3 py-1 text-xs"
                  >
                    <FontAwesomeIcon
                      icon={faUsers}
                      className="text-secondText text-[10px]"
                    />
                    {c.name}
                  </span>
                ))}
              </div>
            </section>
          ) : null}

          {/* Tags */}
          {tags.length > 0 ? (
            <section className="border-t border-border pt-6 mt-6">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-secondText mb-3">
                {t("home.book.modal.section.tags")}
              </h4>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 rounded-full bg-primaryMuted text-primary border border-primary/30 px-3 py-1 text-xs font-medium"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </section>
          ) : null}

          {/* Story notes — author-surfaced prewriting scratch. */}
          {notes ? (
            <section className="border-t border-border pt-6 mt-6">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-secondText mb-3 inline-flex items-center gap-2">
                <FontAwesomeIcon icon={faNoteSticky} className="text-[10px]" />
                {t("home.book.modal.section.notes")}
              </h4>
              <div className="rounded-xl border border-border bg-surfaceAlt p-4">
                <p className="text-sm text-text whitespace-pre-wrap leading-relaxed">
                  {notes}
                </p>
              </div>
            </section>
          ) : null}

          {/* Table of contents */}
          {chapters.length > 0 ? (
            <section className="border-t border-border pt-6 mt-6">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-secondText mb-3 inline-flex items-center gap-2">
                <FontAwesomeIcon icon={faList} className="text-[10px]" />
                {t("home.book.modal.section.toc", { count: chapters.length })}
              </h4>
              <ol className="rounded-xl border border-border divide-y divide-border bg-surface">
                {chapters.map((ch, i) => (
                  <li
                    key={ch.id ?? i}
                    className="flex items-center gap-3 px-4 py-2.5"
                  >
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-surfaceAlt text-secondText text-[11px] font-semibold shrink-0">
                      {i + 1}
                    </span>
                    <p className="flex-1 min-w-0 text-sm text-text truncate">
                      {ch.title || t("home.book.modal.untitled_chapter", { index: i + 1 })}
                    </p>
                    {ch.word_count ? (
                      <span className="text-[11px] text-secondText shrink-0">
                        {t("home.book.modal.words", { count: ch.word_count })}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ol>
            </section>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
};

// Map the English enum values CreateProject persists onto the
// i18n keys the picker used at write time. Without this the book
// modal renders the raw stored value ("Fiction", "French",
// "Adults") regardless of the reader's locale, so a FR-locale
// reader sees "French" instead of "Français" next to the
// language label.
//
// New options added to CreateProject must have a row here too —
// otherwise the modal falls through to the raw value, which
// renders something readable but in the writer's source locale.
// Keep this table aligned with categoryOptions / languageOptions /
// targetAudienceOptions in CreateProject.jsx.
const ENUM_LABEL_KEYS = {
  category: {
    "Biography": "create_project.category.biography",
    "Fiction": "create_project.category.fiction",
    "Non-Fiction": "create_project.category.non_fiction",
  },
  audience: {
    "Children": "create_project.audience.children",
    "Teenagers": "create_project.audience.teenagers",
    "Adults": "create_project.audience.adults",
    "Elders": "create_project.audience.elders",
  },
  language: {
    "English": "create_project.language.english",
    "French": "create_project.language.french",
  },
  // ``genre`` doesn't have a fixed enum (writers free-type or pick
  // from a long catalogue), so no map is needed — the value
  // passes through. Listed here so a future enum-ification only
  // requires adding the rows.
  genre: {},
};

function localizeEnumValue(t, kind, value) {
  if (!value || typeof value !== "string") return value;
  const map = ENUM_LABEL_KEYS[kind];
  const key = map && map[value];
  if (!key) return value;
  const localised = t(key);
  // ``t`` returns the key string when no translation exists —
  // fall back to the raw value in that case so we never render
  // a key like "create_project.language.english" to the user.
  return localised && localised !== key ? localised : value;
}

export default Modal;
